/**
 * Manual mode — dart by dart, summed as they go.
 *
 * Slower than Assist, and worth it in the two places Assist is weakest: a
 * marker who does not want to do the arithmetic, and a checkout, where the
 * number of darts thrown is the figure the per-dart average depends on. Here
 * it is known from the slots themselves, so a checkout submits straight away
 * rather than stopping to ask.
 *
 * Submitting on one or two darts is normal, not an edge case — it is what a
 * finish looks like.
 */

const SUITS = [
  { key: 'S', label: 'Single', factor: 1 },
  { key: 'D', label: 'Double', factor: 2 },
  { key: 'T', label: 'Treble', factor: 3 },
];

const NUMBERS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
  11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
];

export default function DartEntry({ darts, suit, onSuit, onDart, onUndo, onSubmit, disabled }) {
  const total = darts.reduce((sum, dart) => sum + dart.value, 0);
  const full  = darts.length >= 3;

  const add = (label, value) => {
    if (full) return;
    onDart({ label, value });
  };

  // The bull is its own pair of targets, not a number with a suit on it: a
  // treble bull does not exist, and offering one invites a score that cannot
  // have been thrown.
  const bullDisabled = disabled || full;

  return (
    <div className="hd-darts">
      <div className="hd-darts-slots" aria-live="polite">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`hd-dart-slot ${darts[i] ? 'hd-dart-filled' : ''}`}>
            {darts[i]?.label ?? '—'}
          </div>
        ))}
        <div className="hd-darts-total">{total}</div>
      </div>

      <div className="hd-darts-suits" role="group" aria-label="Multiplier">
        {SUITS.map((option) => (
          <button
            key={option.key}
            type="button"
            className={`hd-key hd-suit ${suit === option.key ? 'hd-suit-on' : ''}`}
            aria-pressed={suit === option.key}
            disabled={disabled}
            onClick={() => onSuit(option.key)}
          >
            {option.key}
          </button>
        ))}
      </div>

      <div className="hd-darts-numbers">
        {NUMBERS.map((n) => {
          const factor = SUITS.find((s) => s.key === suit).factor;
          return (
            <button
              key={n}
              type="button"
              className="hd-key"
              disabled={disabled || full}
              onClick={() => add(`${suit}${n}`, n * factor)}
            >
              {n}
            </button>
          );
        })}

        <button type="button" className="hd-key" disabled={bullDisabled}
                onClick={() => add('25', 25)}>25</button>
        <button type="button" className="hd-key" disabled={bullDisabled}
                onClick={() => add('BULL', 50)}>Bull</button>
        <button type="button" className="hd-key" disabled={bullDisabled}
                onClick={() => add('MISS', 0)}>Miss</button>
      </div>

      <div className="hd-darts-actions">
        <button
          type="button"
          className="hd-key hd-key-quiet"
          disabled={disabled || darts.length === 0}
          onClick={onUndo}
        >
          Undo dart
        </button>

        <button
          type="button"
          className="hd-key hd-key-go"
          disabled={disabled || darts.length === 0}
          onClick={() => onSubmit(total, darts.length)}
        >
          Enter {darts.length > 0 && `(${darts.length} dart${darts.length === 1 ? '' : 's'})`}
        </button>
      </div>
    </div>
  );
}
